import * as React from "react"
import { Link, usePage } from "@inertiajs/react"
import { useTranslation } from "react-i18next"
import {
  HomeIcon,
  FolderIcon,
  LayoutGridIcon,
  ActivityIcon,
  LayoutTemplateIcon,
  BookOpenIcon,
  PanelLeftIcon,
  StarIcon,
  BotIcon,
} from "lucide-react"

import { NavUser } from "@/components/nav-user"
import { OrganizationSwitcher } from "@/components/organization-switcher"
import { Logo } from "@/components/logo"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
  useSidebar,
} from "@/components/ui/sidebar"
import { Button } from "@/components/ui/button"

interface User {
  id: number
  name: string
  email: string
  admin: boolean
  super_admin?: boolean
  organization_role?: string
}

interface ProjectsSidebarProps extends React.ComponentProps<typeof Sidebar> {
  user: User
  starredCount?: number
}

export function ProjectsSidebar({ user, starredCount, ...props }: ProjectsSidebarProps) {
  const { url, props: pageProps } = usePage<{ organization?: { fde_enabled?: boolean; managed_agents_enabled?: boolean } }>()
  const { toggleSidebar } = useSidebar()
  const { t } = useTranslation('common')
  const fdeEnabled = (pageProps as any).organization?.fde_enabled
  const managedAgentsEnabled = (pageProps as any).organization?.managed_agents_enabled
  const isAgentsView = url.includes("kind=agents")

  return (
    <Sidebar collapsible="icon" variant="floating" {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem className="relative">
            {/* Expanded state: Logo links home, toggle button on right */}
            <SidebarMenuButton size="lg" asChild className="hover:bg-transparent active:bg-transparent data-[active=true]:bg-transparent group-data-[collapsible=icon]:hidden">
              <Link href="/">
                <div className="flex flex-1 items-center">
                  <Logo className="h-6" />
                </div>
              </Link>
            </SidebarMenuButton>
            {/* Collapsed state: Logo icon, changes to sidebar icon on hover */}
            <SidebarMenuButton
              size="lg"
              className="hover:bg-accent hidden group-data-[collapsible=icon]:flex justify-center group/logo"
              onClick={toggleSidebar}
              tooltip={t('sidebar.expandSidebar')}
            >
              <Logo iconOnly className="h-6 w-6 group-hover/logo:hidden" />
              <PanelLeftIcon className="h-5 w-5 hidden group-hover/logo:block text-foreground" />
            </SidebarMenuButton>
            {/* Collapse toggle button - only visible when expanded */}
            <Button
              variant="ghost"
              size="icon"
              className="absolute top-2 right-2 h-7 w-7 group-data-[collapsible=icon]:hidden"
              onClick={toggleSidebar}
            >
              <PanelLeftIcon className="h-4 w-4" />
              <span className="sr-only">{t('sidebar.toggleSidebar')}</span>
            </Button>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarContent>
        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {fdeEnabled && (
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip={t('sidebar.fde')} isActive={url.startsWith('/fde')}>
                    <Link href="/fde">
                      <BotIcon />
                      <span>{t('sidebar.fde')}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              )}
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={t('sidebar.home')} isActive={url === '/projects' || url === '/projects/'}>
                  <Link href="/projects">
                    <HomeIcon />
                    <span>{t('sidebar.home')}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={t('sidebar.assets')} isActive={url.startsWith("/organization/assets")}>
                  <Link href="/organization/assets">
                    <FolderIcon />
                    <span>{t('sidebar.assets')}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Projects Section */}
        <SidebarGroup>
          <SidebarGroupLabel>{t('sidebar.projects')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={t('sidebar.allProjects')} isActive={url.includes("filter=active") && !isAgentsView}>
                  <Link href="/projects?filter=active">
                    <LayoutGridIcon />
                    <span>{t('sidebar.allProjects')}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={t('sidebar.starred')} isActive={url.includes("filter=starred")}>
                  <Link href="/projects?filter=starred">
                    <StarIcon />
                    <span>{t('sidebar.starred')}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={t('sidebar.production')} isActive={url.startsWith("/projects/production")}>
                  <Link href="/projects/production">
                    <ActivityIcon />
                    <span>{t('sidebar.production')}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {managedAgentsEnabled && (
          <SidebarGroup>
            <SidebarGroupLabel>{t('sidebar.agents')}</SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                <SidebarMenuItem>
                  <SidebarMenuButton asChild tooltip={t('sidebar.allAgents')} isActive={isAgentsView}>
                    <Link href="/projects?kind=agents">
                      <BotIcon />
                      <span>{t('sidebar.allAgents')}</span>
                    </Link>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}

        {/* Resources Section */}
        <SidebarGroup>
          <SidebarGroupLabel>{t('sidebar.resources')}</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={t('sidebar.templates')} isActive={url.includes("tab=templates")}>
                  <Link href="/projects?tab=templates">
                    <LayoutTemplateIcon />
                    <span>{t('sidebar.templates')}</span>
                  </Link>
                </SidebarMenuButton>
              </SidebarMenuItem>
              <SidebarMenuItem>
                <SidebarMenuButton asChild tooltip={t('sidebar.learn')}>
                  <a href="https://academy.cayu.ai/" target="_blank" rel="noopener noreferrer">
                    <BookOpenIcon />
                    <span>{t('sidebar.learn')}</span>
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Spacer to push footer to bottom */}
        <div className="flex-1" />

      </SidebarContent>

      <SidebarFooter>
        <OrganizationSwitcher />
        <NavUser user={user} />
      </SidebarFooter>
    </Sidebar>
  )
}
